/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import { Price, InLineAlert } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  NegotiableQuoteModel,
  CartItemModel,
} from '@/quote-management/data/models/negotiable-quote-model';
import {
  ProductListTable,
  QuotePricesSummary,
  ItemsQuoted as ItemsQuotedComponent,
} from '@/quote-management/components';
import { CheckWithCircle, WarningFilled } from '@adobe-commerce/elsie/icons';
import { ConfirmationModal } from '@/quote-management/components/ConfirmationModal';
import { LineItemNoteModal } from '@/quote-management/components/LineItemNoteModal';
import { updateQuantities, setLineItemNote } from '@/quote-management/api';
import { removeNegotiableQuoteItems } from '@/quote-management/api/removeNegotiableQuoteItems';

const NOTIFICATION_AUTO_DISMISS_DELAY = 3000;

export interface ItemsQuotedProps extends HTMLAttributes<HTMLDivElement> {
  quoteData?: NegotiableQuoteModel;
  onItemCheckboxChange?: (
    item: CartItemModel,
    isSelected: boolean
  ) => void;
  onItemDropdownChange?: (
    item: CartItemModel,
    action: string
  ) => void;
  onUpdate?: (e: SubmitEvent) => void;
  onRemoveItemsRef?: (
    handler: (items: CartItemModel[]) => void
  ) => void;
  onRemoveModalStateChange?: (isOpen: boolean) => void;
  slots?: {
    ProductListTable?: SlotProps<{
      items: NegotiableQuoteModel['items'];
      canEdit: boolean;
      readOnly?: boolean;
      onItemCheckboxChange?: (
        item: CartItemModel,
        isSelected: boolean
      ) => void;
      onItemDropdownChange?: (
        item: CartItemModel,
        action: string
      ) => void;
      onQuantityChange?: (
        item: CartItemModel,
        newQuantity: number
      ) => void;
      onUpdate?: (e: SubmitEvent) => void;
      dropdownSelections?: Record<string, string | undefined>;
    }>;
    QuotePricesSummary?: SlotProps<{
      items: NegotiableQuoteModel['items'];
      prices: NegotiableQuoteModel['prices'];
    }>;
  };
}

export const ItemsQuoted: Container<ItemsQuotedProps> = ({
  quoteData: initialData,
  onItemCheckboxChange,
  onItemDropdownChange,
  onRemoveItemsRef,
  onRemoveModalStateChange,
  slots,
  ...props
}) => {
  const [quoteData, setQuoteData] = useState<NegotiableQuoteModel | undefined>(
    initialData
  );
  const [quantityChanges, setQuantityChanges] = useState<{
    [itemUid: string]: number;
  }>({});
  const [selectedItem, setSelectedItem] =
    useState<CartItemModel | null>(null);
  const [isUpdateQuantitiesModalOpen, setIsUpdateQuantitiesModalOpen] =
    useState(false);
  const [isLineItemNoteModalOpen, setIsLineItemNoteModalOpen] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [notificationState, setNotificationState] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  // State for remove items functionality
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState<CartItemModel[]>(
    []
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeNotificationState, setRemoveNotificationState] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [dropdownSelections, setDropdownSelections] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dictionary = useText({
    subtotal: 'NegotiableQuote.Manage.quotePricesSummary.subtotal.excludingTax',
    grandTotal:
      'NegotiableQuote.Manage.quotePricesSummary.grandTotal.includingTax',
    appliedTaxes: 'NegotiableQuote.Manage.quotePricesSummary.appliedTaxes',
    modalTitle: 'NegotiableQuote.Manage.updateQuantitiesModal.title',
    modalDescription:
      'NegotiableQuote.Manage.updateQuantitiesModal.description',
    modalCancelButton:
      'NegotiableQuote.Manage.updateQuantitiesModal.cancelButton',
    modalUpdateButton:
      'NegotiableQuote.Manage.updateQuantitiesModal.updateButton',
    successHeading:
      'NegotiableQuote.Manage.updateQuantitiesModal.successHeading',
    successMessage:
      'NegotiableQuote.Manage.updateQuantitiesModal.successMessage',
    errorHeading: 'NegotiableQuote.Manage.updateQuantitiesModal.errorHeading',
    errorMessage: 'NegotiableQuote.Manage.updateQuantitiesModal.errorMessage',
    removeModalTitle: 'NegotiableQuote.Manage.removeItemsModal.title',
    removeModalDescription:
      'NegotiableQuote.Manage.removeItemsModal.description',
    removeModalCancelButton:
      'NegotiableQuote.Manage.removeItemsModal.cancelButton',
    removeModalConfirmButton:
      'NegotiableQuote.Manage.removeItemsModal.confirmButton',
    removeModalConfirmButtonRemoving:
      'NegotiableQuote.Manage.removeItemsModal.confirmButtonRemoving',
    removeSuccessHeading:
      'NegotiableQuote.Manage.removeItemsModal.successHeading',
    removeSuccessMessage:
      'NegotiableQuote.Manage.removeItemsModal.successMessage',
    removeErrorHeading: 'NegotiableQuote.Manage.removeItemsModal.errorHeading',
    removeErrorMessage: 'NegotiableQuote.Manage.removeItemsModal.errorMessage',
  });

  useEffect(() => {
    const quoteDataEvent = events.on(
      'quote-management/quote-data',

      (data: { quote: NegotiableQuoteModel }) => {
        setQuoteData(data.quote);
        setQuantityChanges({});
        setNotificationState({ type: null, message: '' });
        setDropdownSelections({});
      },

      {
        eager: true,
      }
    );
    return () => quoteDataEvent?.off();
  }, []);

  useEffect(() => {
    const quantitiesUpdatedEvent = events.on(
      'quote-management/quantities-updated',
      (data: { quote: NegotiableQuoteModel }) => {
        setQuoteData(data.quote);
        setQuantityChanges({});
        setNotificationState({
          type: 'success',
          message: dictionary.successMessage,
        });
        // Auto-close success notification after 3 seconds
        setTimeout(() => {
          setIsUpdateQuantitiesModalOpen(false);
          setNotificationState({ type: null, message: '' });
        }, 3000);
      }
    );
    return () => quantitiesUpdatedEvent?.off();
  }, [dictionary.successMessage]);

  // Listen for quote items removed event
  useEffect(() => {
    const itemsRemovedEvent = events.on(
      'quote-management/quote-items-removed',
      (data: { quote: NegotiableQuoteModel }) => {
        setQuoteData(data.quote);
        setItemsToRemove([]);
        setIsRemoving(false);
        setRemoveNotificationState({
          type: 'success',
          message: dictionary.removeSuccessMessage,
        });
        // Auto-close success notification after 3 seconds
        setTimeout(() => {
          setIsRemoveModalOpen(false);
          setRemoveNotificationState({ type: null, message: '' });
          onRemoveModalStateChange?.(false);
        }, NOTIFICATION_AUTO_DISMISS_DELAY);
      }
    );
    return () => itemsRemovedEvent?.off();
  }, [dictionary.removeSuccessMessage, onRemoveModalStateChange]);

  // Unified handler for removing items (single or multiple)
  const handleRemoveItems = useCallback((items: CartItemModel[]) => {
    if (!items || items.length === 0) {
      return;
    }
    setItemsToRemove(items);
    setRemoveNotificationState({ type: null, message: '' });
    setIsRemoveModalOpen(true);
  }, []);

  // Expose handler to parent component
  useEffect(() => {
    onRemoveItemsRef?.(handleRemoveItems);
  }, [handleRemoveItems, onRemoveItemsRef]);

  const handleDismissRemoveBanner = () => {
    setRemoveNotificationState({ type: null, message: '' });
  };

  const handleItemDropdownChange = (
    item: CartItemModel,
    action: string
  ) => {
    const cartItem = item;
    // Handle edit action - open line item note modal
    if (action === 'edit') {
      setSelectedItem(cartItem);
      setIsLineItemNoteModalOpen(true);
      // Reset dropdown immediately
      setDropdownSelections((prev) => ({ ...prev, [cartItem.uid]: '' }));
      return;
    }

    // Handle remove action
    if (action === 'remove') {
      setDropdownSelections((prev) => ({
        ...prev,
        [cartItem.uid]: action,
      }));
      handleRemoveItems([cartItem]); // Use unified handler
      onItemDropdownChange?.(cartItem, action);
      // Reset dropdown immediately (consistent with edit action)
      setDropdownSelections((prev) => ({ ...prev, [cartItem.uid]: '' }));
      return;
    }

    // For any other actions, pass through to parent (but ignore empty/placeholder selections)
    if (action) {
      onItemDropdownChange?.(cartItem, action);
    }

    // Clear dropdown selection for non-edit/remove actions
    /* istanbul ignore next: defensive cleanup - custom actions shouldn't have dropdown state due to proper cleanup in edit/remove handlers */
    setDropdownSelections((prev) => {
      if (!(cartItem.uid in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[cartItem.uid];
      return next;
    });
  };

  const handleConfirmRemove = async () => {
    /* istanbul ignore next */
    if (!quoteData || itemsToRemove.length === 0) {
      return;
    }

    const uidsToRemove = itemsToRemove.map((item) => item.uid);

    setIsRemoving(true);
    setRemoveNotificationState({ type: null, message: '' });

    try {
      await removeNegotiableQuoteItems({
        quoteUid: quoteData.uid,
        quoteItemUids: uidsToRemove,
      });
      // Success is handled by the event listener
    } catch (err) {
      const errorMessage =
        /* istanbul ignore next */
        err instanceof Error ? err.message : dictionary.removeErrorMessage;
      setRemoveNotificationState({
        type: 'error',
        message: errorMessage,
      });
      // Clear dropdown selections for failed items (single item only)
      /* istanbul ignore next */ // UI-driven state; covered by tests but line sometimes mis-attributed in source maps
      if (itemsToRemove.length === 1) {
        const failedItem = itemsToRemove[0];
        setDropdownSelections((prev) => {
          if (!(failedItem.uid in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[failedItem.uid];
          return next;
        });
      }
      setIsRemoving(false);
    }
  };

  const handleCancelRemove = () => {
    setIsRemoveModalOpen(false);
    setRemoveNotificationState({ type: null, message: '' });
    onRemoveModalStateChange?.(false);
    // Clear dropdown selections for single item cancellation
    /* istanbul ignore next */ // UI-driven state; covered by tests but line sometimes mis-attributed in source maps
    if (itemsToRemove.length === 1) {
      const cancelledItem = itemsToRemove[0];
      setDropdownSelections((prev) => {
        if (!(cancelledItem.uid in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[cancelledItem.uid];
        return next;
      });
    }
    setItemsToRemove([]);
  };

  // Handle modal close
  const handleModalClose = useCallback(() => {
    // Clean up dropdown selection for the selected item before clearing it
    /* istanbul ignore else: defensive check - selectedItem should always be truthy when modal is open */
    if (selectedItem) {
      setDropdownSelections((prev) => {
        const next = { ...prev };
        delete next[selectedItem.uid];
        return next;
      });
    }
    setIsLineItemNoteModalOpen(false);
    setSelectedItem(null);
    setIsSubmitting(false);
    setModalErrorMessage('');
  }, [selectedItem]);

  // Handle modal confirm - submit note and quantity
  const handleModalConfirm = useCallback(
    async (note: string, quantity: number) => {
      /* istanbul ignore next: defensive guard for async race conditions - unreachable in normal flow as modal only renders with selectedItem and quoteData */
      if (!selectedItem || !quoteData) return;

      setIsSubmitting(true);
      try {
        // Check if quantity has changed from the original
        const quantityChanged = quantity !== selectedItem.quantity;

        // If quantity changed, update it first
        if (quantityChanged) {
          await updateQuantities({
            quoteUid: quoteData.uid,
            items: [{ quoteItemUid: selectedItem.uid, quantity }],
          });
        }

        // Then set the line item note
        await setLineItemNote({
          quoteUid: quoteData.uid,
          itemUid: selectedItem.uid,
          note,
          quantity,
        });

        // Close modal on success
        handleModalClose();
      } catch (error) {
        console.error('Failed to set line item note:', error);
        // Extract error message from API
        const errorMessage =
          /* istanbul ignore next */
          error instanceof Error
            ? error.message
            : 'Unable to update the item. Please try again.';
        setModalErrorMessage(errorMessage);
        setIsSubmitting(false);
      }
    },
    [selectedItem, quoteData, handleModalClose]
  );

  if (!quoteData) {
    return <ItemsQuotedComponent loading={true} />;
  }

  const canEdit = Boolean(quoteData.canUpdateQuote);

  const handleQuantityChange = (
    item: CartItemModel,
    newQuantity: number
  ) => {
    setQuantityChanges((prev) => ({
      ...prev,
      [item.uid]: newQuantity,
    }));
  };

  const handleUpdate = (e: SubmitEvent) => {
    e.preventDefault();
    setNotificationState({ type: null, message: '' });
    setIsUpdateQuantitiesModalOpen(true);
  };

  const handleConfirmUpdate = async () => {
    /* istanbul ignore next */
    if (!quoteData) {
      return;
    }

    if (Object.keys(quantityChanges).length === 0) {
      setIsUpdateQuantitiesModalOpen(false);
      return;
    }

    // Clear any previous notifications
    setNotificationState({ type: null, message: '' });

    const items = Object.entries(quantityChanges).map(
      ([quoteItemUid, quantity]) => ({
        quoteItemUid,
        quantity,
      })
    );

    try {
      await updateQuantities({
        quoteUid: quoteData.uid,
        items,
      });
      // Success is handled by the event listener
    } catch (err) {
      const errorMessage =
        /* istanbul ignore next */
        err instanceof Error ? err.message : dictionary.errorMessage;
      setNotificationState({
        type: 'error',
        message: errorMessage,
      });
      console.error('Failed to update quantities:', err);
    }
  };

  const handleCancelUpdate = () => {
    setIsUpdateQuantitiesModalOpen(false);
    setNotificationState({ type: null, message: '' });
  };

  const handleDismissBanner = () => {
    setNotificationState({ type: null, message: '' });
  };

  const quotePricesSummaryEntries = [];

  quoteData.prices.subtotalExcludingTax &&
    quotePricesSummaryEntries.push({
      label: dictionary.subtotal,
      id: 'subtotal',
      value: (
        <Price
          amount={quoteData.prices.subtotalExcludingTax.value}
          currency={quoteData.prices.subtotalExcludingTax.currency}
          weight="normal"
        />
      ),
    });

  quoteData.prices.grandTotal &&
    quotePricesSummaryEntries.push({
      label: dictionary.grandTotal,
      id: 'total',
      value: (
        <Price
          amount={quoteData.prices.grandTotal.value}
          currency={quoteData.prices.grandTotal.currency}
        />
      ),
      strong: true,
    });

  // Create confirmation banner based on notification state
  const confirmationBanner =
    notificationState.type === 'success' ? (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<CheckWithCircle />}
        heading={dictionary.successHeading}
        description={notificationState.message}
        onDismiss={handleDismissBanner}
        data-testid="update-quantities-success-banner"
      />
    ) : notificationState.type === 'error' ? (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.errorHeading}
        description={notificationState.message}
        onDismiss={handleDismissBanner}
        data-testid="update-quantities-error-banner"
      />
    ) : null;

  // Create wrapper for checkbox change handler
  const handleItemCheckboxChange = onItemCheckboxChange
    ? (item: CartItemModel, isSelected: boolean) => {
        onItemCheckboxChange(item, isSelected);
      }
    : undefined;

  // Create remove confirmation banner based on notification state
  const removeConfirmationBanner =
    removeNotificationState.type === 'success' ? (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<CheckWithCircle />}
        heading={dictionary.removeSuccessHeading}
        description={removeNotificationState.message}
        onDismiss={handleDismissRemoveBanner}
        data-testid="remove-items-success-banner"
      />
    ) : removeNotificationState.type === 'error' ? (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.removeErrorHeading}
        description={removeNotificationState.message}
        onDismiss={handleDismissRemoveBanner}
        data-testid="remove-items-error-banner"
      />
    ) : null;

  // Create modal error banner
  const modalErrorBanner = modalErrorMessage ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.errorHeading}
      description={modalErrorMessage}
      onDismiss={() => setModalErrorMessage('')}
      data-testid="line-item-note-error-banner"
    />
  ) : null;

  return (
    <>
      <ItemsQuotedComponent
        data-testid="items-quoted-container"
        {...props}
        loading={false}
        table={
          <Slot
            name="ProductListTable"
            slot={slots?.ProductListTable}
            context={{
              items: quoteData.items,
              canEdit,
              readOnly: quoteData.readOnly,
              onItemCheckboxChange,
              onItemDropdownChange: handleItemDropdownChange,
              onQuantityChange: handleQuantityChange,
              onUpdate: handleUpdate,
              dropdownSelections,
            }}
          >
            <ProductListTable
              items={quoteData.items}
              canEdit={canEdit}
              onItemCheckboxChange={handleItemCheckboxChange}
              readOnly={quoteData.readOnly}
              onItemDropdownChange={handleItemDropdownChange}
              onQuantityChange={handleQuantityChange}
              onUpdate={handleUpdate}
              dropdownSelections={dropdownSelections}
            />
          </Slot>
        }
        pricesSummary={
          <Slot
            name="QuotePricesSummary"
            slot={slots?.QuotePricesSummary}
            context={{
              items: quoteData.items,
              prices: quoteData.prices,
            }}
          >
            <QuotePricesSummary entries={quotePricesSummaryEntries} />
          </Slot>
        }
      />

      {/* Update Quantities Modal */}
      <ConfirmationModal
        open={isUpdateQuantitiesModalOpen}
        title={dictionary.modalTitle}
        message={dictionary.modalDescription}
        cancelLabel={dictionary.modalCancelButton}
        confirmLabel={dictionary.modalUpdateButton}
        onCancel={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
        confirmationBanner={confirmationBanner}
        data-testid="update-quantities-modal"
      />

      {/* Remove Items Modal */}
      <ConfirmationModal
        open={isRemoveModalOpen}
        title={dictionary.removeModalTitle}
        message={dictionary.removeModalDescription}
        cancelLabel={dictionary.removeModalCancelButton}
        confirmLabel={
          isRemoving
            ? dictionary.removeModalConfirmButtonRemoving
            : dictionary.removeModalConfirmButton
        }
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        confirmationBanner={removeConfirmationBanner}
        data-testid="remove-items-modal"
      />
      {/* Line Item Note Modal */}
      {selectedItem && (
        <LineItemNoteModal
          open={isLineItemNoteModalOpen}
          item={selectedItem}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          isSubmitting={isSubmitting}
          errorBanner={modalErrorBanner || undefined}
        />
      )}
    </>
  );
};
